import React, { forwardRef } from "react";
import DOMPurify from "dompurify";
import {
  firstValue,
  getConfigValue,
  getDocumentAmount,
  getDocumentDate,
  getDocumentNumber,
  getDocumentStatus,
  getPartyName,
  initials,
  money,
  text,
} from "./documentHelpers";
import { resolveDocumentSubtotal } from "./print/documentPrintHelpers";

const getItems = (row) => {
  const possible = [row.items, row.itemRows, row.lineItems, row.itemDetails, row.products].find(Array.isArray);
  if (possible) return possible;
  return [
    {
      itemsDetails: row.itemsDetails || row.itemName || row.expenseAccount || row.reference || row.orderNumber || row.invoice || "Document line item",
      quantity: row.quantity ?? 1,
      rate: row.rate ?? getDocumentAmount(row, {}),
      amount: row.amount ?? row.total,
      hsnCode: row.hsnCode,
    },
  ];
};

const richTextMarkup = (value, fallback = "-") => ({
  __html: DOMPurify.sanitize(String(text(value, fallback))),
});

const DocumentPreview = forwardRef(({ row = {}, config = {} }, ref) => {
  const status = getDocumentStatus(row, config);
  const items = getItems(row);
  const subtotal = resolveDocumentSubtotal(row, items);
  const validValue = getConfigValue(config, "validValue", row);
  const partyLabel = config.partyLabel || "Customer";

  return (
    <section className="document-preview-card document-print-scope" ref={ref}>
      <div className="document-preview-head">
        <div className="document-company-block">
          <span>{initials(firstValue(row, ["companyName", "organizationName"], "ZARP"))}</span>
          <div>
            <h3>{text(firstValue(row, ["organizationName", "companyName"], "Company"))}</h3>
            <p>{text(firstValue(row, ["organizationAddress", "address", "deliverTo"], ""))}</p>
          </div>
        </div>
        <div className="document-title-block">
          <h3>{String(config.singular || "Document").toUpperCase()}</h3>
          <span className={`document-pill ${status.tone}`}>{status.label}</span>
        </div>
      </div>

      <div className="document-bill-strip">
        <div className="is-wide">
          <span>{partyLabel}</span>
          <strong>{getPartyName(row, config)}</strong>
          <p>{text(firstValue(row, ["billingAddress", "deliveryAddress", "deliverTo", "address"], ""))}</p>
        </div>
        <div>
          <span>Phone</span>
          <strong>{text(firstValue(row, ["phone", "mobile", "billPhone"], ""))}</strong>
        </div>
        <div>
          <span>Email</span>
          <strong>{text(firstValue(row, ["email", "customerEmail", "vendorEmail"], ""))}</strong>
        </div>
      </div>

      <div className="document-preview-meta">
        <div>
          <span>{config.singular || "Document"} No</span>
          <strong>{getDocumentNumber(row, config)}</strong>
        </div>
        <div>
          <span>Date</span>
          <strong>{getDocumentDate(row, config)}</strong>
        </div>
        <div>
          <span>{config.validLabel || "Reference"}</span>
          <strong>{text(validValue || firstValue(row, ["reference", "orderNumber"], ""))}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{status.label}</strong>
        </div>
      </div>

      <div className="document-items-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Item & Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.itemsDetails || item.itemName || index}-${index}`}>
                <td>{index + 1}</td>
                <td>{text(item.itemsDetails || item.itemName || item.name || item.description)}</td>
                <td>{text(item.quantity ?? item.qty ?? 1)}</td>
                <td>{money(item.rate ?? item.price)}</td>
                <td>{money(item.amount ?? item.total ?? Number(item.quantity ?? item.qty ?? 1) * Number(item.rate ?? item.price ?? 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="document-preview-bottom">
        <article>
          <h4>Notes</h4>
          <p>{text(firstValue(row, ["customerNote", "notes", "note"], "Thank you for your business."))}</p>
          <h4 className="document-terms-heading">Terms & Conditions</h4>
          <div
            className="document-rich-text"
            dangerouslySetInnerHTML={richTextMarkup(firstValue(row, ["termsAndCondition", "terms", "paymentTerms"], "Standard terms apply."))}
          />
        </article>
        <article className="document-total-card">
          <div>
            <span>Sub Total</span>
            <strong>{money(subtotal)}</strong>
          </div>
          {row.discount && (
            <div>
              <span>Discount</span>
              <strong>{money(row.discount)}</strong>
            </div>
          )}
          <div className="grand-total">
            <span>Total</span>
            <strong>{money(getDocumentAmount(row, config))}</strong>
          </div>
        </article>
      </div>
    </section>
  );
});

export default DocumentPreview;
